import { PlayResponse } from '@models/play-response';
import { SceneKey } from '@models/scene-keys';
import EventEngine from '@services/core/EventEngine';
import { TranslationService } from '@services/core/TranslationService';
import { createDice } from '../ui/Dice';
import { DPR, UI_DEPTH } from '@constants/common.const';
import { ApiService } from '@services/api/ApiService';
import { GameConfigService } from '@services/core/GameConfigService';

export default class DiceOverlay extends Phaser.Scene {
  private t: TranslationService;

  constructor() {
    super(SceneKey.DiceOverlay);
    this.t = new TranslationService();
  }

  private bg!: Phaser.GameObjects.Rectangle;

  private maskGfx!: Phaser.GameObjects.Graphics;
  private geomMask!: Phaser.Display.Masks.GeometryMask;
  private rollResponse?: PlayResponse;

  private diceApi?: {
    roll: (roll: number, cb: (roll: number) => void) => void;
    dice: Phaser.GameObjects.Mesh;
  };

  private isRolling = false;
  private startedRoll = false;

  private titleText!: Phaser.GameObjects.Text;
  private subtitleText!: Phaser.GameObjects.Text;

  private cx = 0;
  private cy = 0;
  private endR = 0;

  private inputBlocker!: Phaser.GameObjects.Rectangle;
  private configService = new GameConfigService();

  create() {
    // If this scene is re-used (launched without stopping), reset runtime state
    this.startedRoll = false;
    this.isRolling = false;

    const w = this.scale.width;
    const h = this.scale.height;

    // Cleanup if something is still around
    this.diceApi?.dice?.destroy();
    this.diceApi = undefined;

    // Fullscreen blue background
    const overlayColor =
      this.configService.getColor('--z-dice-overlay-background') ??
      this.configService.getColor('--color-accent-1');
    this.bg = this.add.rectangle(w / 2, h / 2, w, h, overlayColor, 1);

    // Circular mask — drawn once, then scaled to animate reveal (no per-frame clear+redraw)
    this.maskGfx = this.add.graphics();
    this.maskGfx.setVisible(false);
    this.geomMask = this.maskGfx.createGeometryMask();
    this.bg.setMask(this.geomMask);

    // DICE POSITIONING
    // 64px from the screen bottom
    const diceX = w / 2;
    const diceY = h - 100 * DPR;

    // 2. DICE SIZING
    // To get a 128px visual size, we pass 128 * DPR to the helper.
    // Note: Phaser Mesh scale is a multiplier, not pixels.
    // We adjust the helper below, but here we calculate the multiplier.
    const diceSizePx = 128 * DPR;

    // 3. MASK POSITIONING (Independent of Dice Y)
    // Target height: 40% of screen height
    this.endR = h * 0.4;
    this.cx = w / 2;
    // We place the center so the circle's top edge reaches the desired height
    // but its bottom edge covers the dice area.
    this.cy = h - this.endR * 0.2;

    // Reveal radius: only grow to ~40% of the screen height (not full screen)
    // Keep a small minimum so it still looks OK on very small screens.
    this.endR = Math.max(h * 0.4, 180 * DPR);

    // Place the reveal origin slightly below the dice so the capped radius still reaches it.
    // (If we keep the origin below the screen, a 40% radius would never reach the dice.)
    this.cx = diceX;
    this.cy = diceY + this.endR * 0.4;

    // Position the Graphics at the reveal center so scale animates around that point
    this.maskGfx.setPosition(this.cx, this.cy);
    // Draw circle at local origin (0,0) — drawn once, never redrawn
    this.maskGfx.fillStyle(0xffffff, 1);
    this.maskGfx.fillCircle(0, 0, this.endR);
    this.maskGfx.setScale(0);

    this.diceApi = createDice(
      diceX,
      diceY,
      this,
      1500,
      diceSizePx,
      this.geomMask,
    );

    // Dice entrance animation: fade in and slide up from bottom-center.
    // (Prevents the dice from appearing instantly and sitting still before rolling.)
    const dice = this.diceApi.dice;
    const diceFinalY = diceY;
    const diceStartY = h + diceSizePx / 2;
    dice.setAlpha(0);
    dice.y = diceStartY;

    this.tweens.add({
      targets: dice,
      y: diceFinalY,
      alpha: 1,
      duration: 800,
      ease: 'Cubic.easeOut',
    });

    // subtitle: 32px above dice
    // title: 16px above subtitle
    const subtitleY = diceY - diceSizePx / 4 - 32 * DPR;
    const steps = 0;
    const textColor =
      this.configService.getColorHex('--z-dice-result-text-color') ??
      this.configService.getColorHex('--color-neutral-100');
    this.subtitleText = this.add
      .text(
        w / 2,
        subtitleY,
        this.t.translateArgs('diceOverlay.subtitle', steps.toString()),
        {
          fontFamily: 'korolev, sans-serif',
          fontSize: `${16 * DPR}px`,
          color: textColor,
        },
      )
      .setOrigin(0.5)
      .setAlpha(0);

    const titleY = subtitleY - this.subtitleText.height - 16 * DPR;

    this.titleText = this.add
      .text(w / 2, titleY, this.t.translate('diceOverlay.title'), {
        fontFamily: 'korolev, sans-serif',
        fontSize: `${24 * DPR}px`,
        color: textColor,
        fontStyle: '700',
      })
      .setOrigin(0.5)
      .setAlpha(0);

    // mask UI too
    this.titleText.setMask(this.geomMask);
    this.subtitleText.setMask(this.geomMask);

    // --- Reveal animation (starts roll before full reveal) ---
    // Tween scale 0→1 on the pre-drawn circle — no earcut per frame
    const rollStartScale = 0.5;

    this.tweens.add({
      targets: this.maskGfx,
      scaleX: 1,
      scaleY: 1,
      duration: 900,
      ease: 'Cubic.easeOut',
      onUpdate: () => {
        if (!this.startedRoll && this.maskGfx.scaleX >= rollStartScale) {
          this.startedRoll = true;
          this.rollOnce();
        }
      },
      onComplete: () => {
        if (!this.startedRoll) {
          this.startedRoll = true;
          this.rollOnce();
        }
      },
    });

    // Ensure this overlay is fully modal: a transparent interactive layer that eats all input.
    // (Without this, clicks can pass through to underlying DOM/Phaser UI.)
    this.inputBlocker = this.add.rectangle(w / 2, h / 2, w, h, 0x000000, 0);
    this.inputBlocker
      .setInteractive({ useHandCursor: false })
      .on('pointerdown', (p: Phaser.Input.Pointer) => {
        p.event?.stopPropagation?.();
      });
    this.inputBlocker.setDepth(UI_DEPTH + 10);

    // cleanup if something externally stops the scene
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.diceApi?.dice?.destroy();
      this.diceApi = undefined;
      this.inputBlocker?.destroy();
    });
  }

  private async rollOnce() {
    if (this.isRolling) return;
    if (!this.diceApi) return;

    this.isRolling = true;

    this.titleText.setAlpha(0);
    this.subtitleText.setAlpha(0);
    const api = new ApiService();
    api
      .play()
      .then((response: PlayResponse) => {
        const rollAction = response?.actions?.find(
          (action) => action.type === 'roll_result',
        );
        if (!response || !rollAction || !rollAction.position) {
          this.scene.start(SceneKey.Error);
          this.isRolling = false;
        }
        const steps = Number(rollAction!.steps);
        if (!Number.isInteger(steps) || steps < 1 || steps > 6) {
          console.error(`Invalid steps from backend: ${steps}`);
          this.scene.start(SceneKey.Error);
          this.isRolling = false;
        }
        this.rollResponse = response;
        this.updateDiceWithResult(steps);
      })
      .catch((e) => {
        console.error('Failed to roll', e);
        this.isRolling = false;
        this.scene.start(SceneKey.Error);
      });
  }

  private updateDiceWithResult(steps: number): void {
    this.diceApi!.roll(steps, (roll: number) => {
      this.isRolling = false;
      this.subtitleText.setText(
        this.t.translateArgs('diceOverlay.subtitle', roll.toString()),
      );

      this.tweens.add({
        targets: [this.titleText, this.subtitleText],
        alpha: 1,
        duration: 180,
        ease: 'Cubic.easeOut',
        onComplete: () => {
          this.scene.get(SceneKey.Game).time.delayedCall(1000, () => {
            this.closeOverlay();
          });
        },
      });
    });
  }

  private closeOverlay() {
    const eventEngine = new EventEngine();

    this.tweens.add({
      targets: this.maskGfx,
      scaleX: 0,
      scaleY: 0,
      duration: 260,
      ease: 'Cubic.easeIn',
      onComplete: () => {
        this.diceApi?.dice?.destroy();
        this.diceApi = undefined;

        const game = this.scene.get(SceneKey.Game);
        game.time.delayedCall(300, () => {
          eventEngine.executeResponse(this.rollResponse!).then(() => {
            this.rollResponse = undefined;
          });
        });
        this.scene.stop();
      },
    });
  }
}
